import { QueryAnnotationType } from "@1js/sydney-types";

/**
 * Normalized inputs for {@link resolveSummarizeAnnotationIdAndType}. Each summarize entry
 * point (SummarizeButtons, CopilotCommandCenterSummarize, executePromptWithQueryAnnotation)
 * gathers these from its own view-state/store sources, but the decision of which id to send
 * and how to type it is shared so the entry points cannot drift apart.
 */
export interface SummarizeAnnotationIdInput {
    /** True when the mail list is organized by conversation (reading pane stacked view). */
    isConversationOrganizedView: boolean;
    /** True when the `rp-summary-for-conversations` flight is enabled for this mailbox. */
    isSummaryForConversationsEnabled: boolean;
    /**
     * The conversation id taken ONLY from an actual conversation reading-pane view state.
     * Callers must pass `undefined` when the rendered pane is a single item (SxS / secondary
     * reading pane / projected windows), so a message item id is never tagged as
     * EmailConversation — which GCI rejects with ErrorInvalidIdMalformed.
     */
    conversationViewStateId: string | undefined;
    /** The latest non-draft message id in the conversation, if resolved. */
    latestConversationItemId: string | undefined;
    /** The open item's id (immutable preferred, else EWS) used as the final fallback. */
    fallbackMessageId: string | undefined;
}

/**
 * Resolves the `{ id, queryAnnotationType }` pair sent to BizChat for thread summarization.
 *
 * The core invariant: only tag `EmailConversation` when an authoritative conversation id is
 * available from the conversation reading-pane state; otherwise send a `Message`-typed item
 * id. This prevents the mislabeled-id handins (heavily skewed toward Monarch SxS/projected
 * windows) that caused GCI `ErrorInvalidIdMalformed` rejections.
 */
export function resolveSummarizeAnnotationIdAndType(input: SummarizeAnnotationIdInput): {
    id: string;
    type: QueryAnnotationType;
} {
    const { isConversationOrganizedView, isSummaryForConversationsEnabled, conversationViewStateId, latestConversationItemId, fallbackMessageId, } = input;
    
    if (isConversationOrganizedView) {
        if (isSummaryForConversationsEnabled && conversationViewStateId) {
            return { id: conversationViewStateId, type: QueryAnnotationType.EmailConversation };
        }
        
        const messageId = latestConversationItemId || fallbackMessageId;
        if (messageId) {
            return { id: messageId, type: QueryAnnotationType.Message };
        }
    }
    
    return { id: fallbackMessageId || "", type: QueryAnnotationType.Message };
}
